import { FileExtension, AlbumContentType } from 'common/enums';
import {
  audioSupportedExtensions,
  ImageSupportedExtensions,
  textSupportedExtensions,
  videoSupportedExtensions,
} from 'config/constants';

export const bytesToMegabytes = (bytes: number) => bytes / 1024 ** 2;
export const formatFileExtensions = (extensions: Array<FileExtension>) =>
  extensions.join(',');
export const getFileExtension = (fileName: string) => {
  const fileNameParts = fileName.split('.');
  if (fileNameParts.length > 1) {
    return `.${fileNameParts.at(-1)}`;
  }
  return '';
};
// The name a memory gets from its uploaded file: "Deed.pdf" → "Deed". Only a
// real trailing extension goes ("a.tar.gz" → "a.tar"); "Notes v2.1 final" and
// ".env" are kept as they are.
export const removeFileExtension = (fileName: string) =>
  fileName.replace(/^(.+)\.[a-z0-9]{1,8}$/i, '$1');
export const isValidSize = (maxSize: number, currentSize: number) =>
  currentSize <= maxSize;

export const isSupportedExtension = (
  extension: string,
  supportedExtensions: Array<FileExtension>,
) => supportedExtensions.some((ext) => ext === extension);

export const getVideoDuration = (file: File): Promise<number> =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.src = URL.createObjectURL(file);
    video.onerror = (error) => reject(error);
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
  });

export const isValidFile = async (
  file: File,
  uploadErrors: {
    generic: string;
    format?: string;
    size?: string;
    duration?: string;
  },
  supportedExt: Array<FileExtension>,
  mbMaxSize: number,
  sMaxDurationAllowed?: number,
): Promise<string> => {
  const fileExtension = getFileExtension(file.name);

  if (!isSupportedExtension(fileExtension.toLocaleLowerCase(), supportedExt)) {
    return uploadErrors.format || uploadErrors.generic;
  }

  if (!isValidSize(mbMaxSize, bytesToMegabytes(file.size))) {
    return uploadErrors.size || uploadErrors.generic;
  }

  if (sMaxDurationAllowed) {
    const isVideo = file.type.startsWith('video/');
    if (isVideo) {
      try {
        const duration = await getVideoDuration(file!);

        if (duration > sMaxDurationAllowed) {
          return uploadErrors.duration || uploadErrors.generic;
        }
      } catch (error) {
        return uploadErrors.generic;
      }
    }
  }
  return '';
};

export const createUrlToDownloadFile = (url: string, name?: string) => {
  const extension = getFileExtension(url);
  const urlWithParam = new URL(url);
  if (name) {
    const nameExtension = name + extension;
    urlWithParam.searchParams.append(
      'disposition',
      `attachment;filename=${encodeURIComponent(nameExtension)}`,
    );
  } else {
    urlWithParam.searchParams.append('disposition', 'attachment');
  }
  return urlWithParam.toString();
};

export const getContentTypeFromFile = (file: File): AlbumContentType => {
  const fileExtension = getFileExtension(file.name).toLowerCase();
  if (videoSupportedExtensions.includes(fileExtension as FileExtension)) {
    return AlbumContentType.video;
  }

  if (audioSupportedExtensions.includes(fileExtension as FileExtension)) {
    return AlbumContentType.audio;
  }

  if (textSupportedExtensions.includes(fileExtension as FileExtension)) {
    return AlbumContentType.write;
  }
  if (ImageSupportedExtensions.includes(fileExtension as FileExtension)) {
    return AlbumContentType.image;
  }

  return AlbumContentType.document;
};
